import type { PrepSummary, RisqPrepTiming } from '@opti-sea/risq';
import { Badge, Group, Progress, Stack, Table, Text } from '@mantine/core';
import type { ProgressCount } from '@opti-sea/risq';
import { useTranslation } from 'react-i18next';
import { usePositionNames } from '../../ui/components';
import { formatDate } from '../../ui/format';
import type { VesselOverview } from './model';

/** "in 75 days · Now: 3-month preparation" */
export function PrepLine({ prep }: { prep: PrepSummary }) {
  const { t } = useTranslation('dashboard');
  if (prep.phase === 'passed') {
    return (
      <Text size="sm" c="red.9">
        {t('inspection.passed')}
      </Text>
    );
  }
  return (
    <Text size="sm">
      {t('inspection.daysAndPhase', {
        days:
          prep.daysLeft === 0
            ? t('inspection.today')
            : t('inspection.daysLeft', { count: prep.daysLeft }),
        phase: t(`inspection.phase.${prep.phase}`),
      })}
    </Text>
  );
}

/** "Last from the last voyage (12 Jan 2027)" or "Last 7 days before". */
export function LastVoyageLine({ prep }: { prep: PrepSummary }) {
  const { t } = useTranslation('dashboard');
  return (
    <Text size="sm">
      {prep.lastFromVoyage
        ? t('inspection.lastFromVoyage', { date: formatDate(prep.starts.last) })
        : t('inspection.lastDefault', { date: formatDate(prep.starts.last) })}
    </Text>
  );
}

function ProgressCell({ progress }: { progress: ProgressCount }) {
  const { t } = useTranslation('dashboard');
  return (
    <Stack gap={2}>
      <Text size="sm">
        {t('progress.doneLeft', {
          done: progress.done,
          left: progress.left,
          percent: progress.percentDone,
        })}
      </Text>
      <Progress value={progress.percentDone} aria-hidden />
    </Stack>
  );
}

/** Vessel progress (owner, 2026-11-03): per position (done / left) and per preparation flag. */
export function ProgressTables({ overview }: { overview: VesselOverview }) {
  const { t } = useTranslation(['dashboard', 'common']);
  const { name } = usePositionNames();
  const meaning = (p: RisqPrepTiming) => t(`common:timings.${p}.meaning`);
  return (
    <Group align="flex-start" grow wrap="wrap">
      <Stack gap="xs" miw={300}>
        <Text fw={700}>{t('progress.byPosition')}</Text>
        <Table>
          <Table.Tbody>
            {overview.byPosition.positions.map(({ code, progress }) => (
              <Table.Tr key={code}>
                <Table.Td>{name(code)}</Table.Td>
                <Table.Td>
                  <ProgressCell progress={progress} />
                </Table.Td>
              </Table.Tr>
            ))}
            {overview.byPosition.unassigned.total === 0 ? null : (
              <Table.Tr>
                <Table.Td>{t('progress.unassigned')}</Table.Td>
                <Table.Td>
                  <ProgressCell progress={overview.byPosition.unassigned} />
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Stack>
      <Stack gap="xs" miw={300}>
        <Text fw={700}>{t('progress.byPhase')}</Text>
        <Table>
          <Table.Tbody>
            {overview.byPhase.map(({ timing: p, progress }) => (
              <Table.Tr key={p}>
                <Table.Td>
                  <Group gap="xs">
                    <Badge variant="outline" tt="none">
                      {t(`common:timings.${p}.short`)}
                    </Badge>
                    <Text size="sm">{meaning(p)}</Text>
                  </Group>
                </Table.Td>
                <Table.Td>
                  {progress.total === 0 ? (
                    <Text size="sm">{t('progress.noQuestions')}</Text>
                  ) : (
                    <ProgressCell progress={progress} />
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        <Text size="sm" c="dimmed">
          {t('progress.noFlagHint')}
        </Text>
      </Stack>
    </Group>
  );
}
